import { useState, useRef, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const localDate = () => new Date().toLocaleDateString("en-CA");
const GLASS_ML = 250;
const W = 60, H = 70;

// Movable, tappable water drop. Tap = log one glass (250ml); drag = reposition.
// Fill level reflects today's hydration vs goal and syncs with the Tracker.
export default function FloatingWaterDrop({ onLogged }) {
  const [total, setTotal] = useState(0);
  const [goal, setGoal] = useState(2500);
  const [pos, setPos] = useState(null);
  const [logging, setLogging] = useState(false);
  const [splash, setSplash] = useState(false);
  const drag = useRef({ on: false, moved: false, sx: 0, sy: 0, ox: 0, oy: 0 });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/water?date=${localDate()}`);
      setTotal(Math.round(data.total_ml || 0));
      setGoal(Math.round(data.goal_ml || 2500));
    } catch (_) {}
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    let saved = null;
    try { saved = JSON.parse(localStorage.getItem("kaaya_waterdrop_pos") || "null"); } catch (_) {}
    const clampX = (x) => Math.max(6, Math.min(window.innerWidth - W - 6, x));
    const clampY = (y) => Math.max(64, Math.min(window.innerHeight - H - 88, y));
    setPos(saved ? { x: clampX(saved.x), y: clampY(saved.y) } : { x: window.innerWidth - W - 14, y: 96 });
  }, []);

  const pct = Math.min(100, Math.round((total / (goal || 1)) * 100));
  const glasses = Math.round(total / GLASS_ML);

  const logGlass = async () => {
    if (logging) return;
    setLogging(true);
    setSplash(true);
    setTotal((t) => t + GLASS_ML);
    setTimeout(() => setSplash(false), 700);
    try {
      await api.post("/water", { amount_ml: GLASS_ML, logged_for: localDate() });
      toast.success(`Logged a glass of water · +${GLASS_ML} ml`);
      onLogged?.();
      load();
    } catch (e) {
      toast.error("Couldn't log water");
      setTotal((t) => Math.max(0, t - GLASS_ML));
    } finally {
      setLogging(false);
    }
  };

  const onDown = (e) => {
    const d = drag.current;
    d.on = true; d.moved = false;
    d.sx = e.clientX; d.sy = e.clientY;
    d.ox = pos?.x || 0; d.oy = pos?.y || 0;
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onMove = (e) => {
    const d = drag.current;
    if (!d.on) return;
    const dx = e.clientX - d.sx, dy = e.clientY - d.sy;
    if (Math.abs(dx) > 4 || Math.abs(dy) > 4) d.moved = true;
    const nx = Math.max(6, Math.min(window.innerWidth - W - 6, d.ox + dx));
    const ny = Math.max(64, Math.min(window.innerHeight - H - 88, d.oy + dy));
    setPos({ x: nx, y: ny });
  };
  const onUp = () => {
    const d = drag.current;
    if (!d.on) return;
    d.on = false;
    if (d.moved) {
      try { localStorage.setItem("kaaya_waterdrop_pos", JSON.stringify(pos)); } catch (_) {}
    } else {
      logGlass();
    }
  };

  if (!pos) return null;
  const fillY = H - (H * pct) / 100;
  const dropPath = "M30 4 C30 4 9 30 9 46 a21 21 0 0 0 42 0 C51 30 30 4 30 4 Z";

  return (
    <div
      data-testid="floating-water-drop"
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={onUp}
      style={{ position: "fixed", left: pos.x, top: pos.y, touchAction: "none", zIndex: 60 }}
      className="cursor-grab active:cursor-grabbing select-none drop-shadow-lg"
      title="Tap to log a glass · Drag to move"
    >
      <div className={`relative ${splash ? "animate-bounce" : ""}`} style={{ width: W, height: H }}>
        <svg width={W} height={H} viewBox="0 0 60 70" className="overflow-visible">
          <defs>
            <clipPath id="kaayaDropClip"><path d={dropPath} /></clipPath>
            <linearGradient id="kaayaWaterGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#7dd3fc" />
              <stop offset="100%" stopColor="#0284c7" />
            </linearGradient>
          </defs>
          <path d={dropPath} fill="rgba(125,211,252,0.16)" stroke="#0ea5e9" strokeWidth="2.5" />
          <g clipPath="url(#kaayaDropClip)">
            <rect x="0" y={fillY} width="60" height={(H * pct) / 100} fill="url(#kaayaWaterGrad)" style={{ transition: "y 0.7s ease, height 0.7s ease" }} />
          </g>
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none pt-1">
          <span className="text-[11px] font-extrabold tabular-nums leading-none" style={{ color: pct > 52 ? "#fff" : "#0369a1" }}>{glasses}</span>
          <span className="text-[7px] font-semibold uppercase tracking-wide leading-none mt-0.5" style={{ color: pct > 62 ? "#e0f2fe" : "#0284c7" }}>glass{glasses === 1 ? "" : "es"}</span>
        </div>
      </div>
    </div>
  );
}
